import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import yeatiHello from "@/assets/yeati-hello-transparent.png";
import type { CampaignAnswer, CampaignOpening, CampaignResponse } from "@/shared/types/featureTeasing.types";
import { Button } from "@/shared/ui/Button";
import {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle,
} from "@/shared/ui/Dialog";
import { ActivationActions } from "./ActivationActions";
import { UpsellActions } from "./UpsellActions";
import { resolvePurpose, selfServicePath, thanksMessage } from "./campaignTeaser.config";
import { prefersReducedMotion, useEndScreenReveal } from "./useEndScreenReveal";

type Props = {
  opening: CampaignOpening
  onShown: (openingId: string) => Promise<unknown>
  onRespond: (openingId: string, answer: CampaignAnswer) => Promise<CampaignResponse>
  onRefresh?: (openingId: string) => Promise<CampaignOpening>
  /** Router navigation, injected by the visit boundary; enables "Je charge moi-même". */
  onNavigate?: (path: string) => void
  returnFocus?: HTMLElement | null
  onClose: () => void
};

const definitive = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

export const CampaignTeaserModal = ({
  opening, onShown, onRespond, onClose, onRefresh, onNavigate, returnFocus,
}: Props) => {
  // A reservation is an immutable content snapshot for the lifetime of this modal.
  const [snapshot, setSnapshot] = useState(opening);
  const [state, setState] = useState<"ready" | "sending" | "retry" | "rejected" | "thanks">("ready");
  const [mediaFailed, setMediaFailed] = useState(false);
  const [shownFailed, setShownFailed] = useState(false);
  const [accountManagerAssigned, setAccountManagerAssigned] = useState(false);
  const intent = useRef<CampaignAnswer | null>(null);
  const sending = useRef(false);
  const mounted = useRef(false);
  const focus = useRef<HTMLElement | null>(null);
  const { campaign, opening_id: openingId } = snapshot;
  const purpose = resolvePurpose(campaign.purpose);
  const activation = purpose === "activation";
  const selfServeTarget = onNavigate ? selfServicePath(campaign.offer?.feature_code) : undefined;
  const { revealed, reveal, videoHandlers } = useEndScreenReveal({
    mediaKind: campaign.media.kind,
    endScreenAtMs: campaign.end_screen_at_ms ?? null,
    enabled: activation,
  });

  useEffect(() => {
    mounted.current = true;
    void onShown(openingId).catch((error: unknown) => {
      if (mounted.current && !intent.current) {
        if (definitive(error)) setState("rejected");
        else setShownFailed(true);
      }
    });
    return () => {
      mounted.current = false;
    };
  }, [onShown, openingId]);

  const respond = async (answer: CampaignAnswer) => {
    if (sending.current || (intent.current && intent.current !== answer)) return;
    intent.current = answer;
    sending.current = true;
    setState("sending");
    setShownFailed(false);
    try {
      const result = await onRespond(openingId, answer);
      if (!mounted.current) return;
      if (result.response === "interested") {
        setAccountManagerAssigned(result.account_manager_assigned === true);
        setState("thanks");
        return;
      }
      if (result.response === "self_served" && selfServeTarget) {
        // La navigation part en transition et peut s'appliquer après la fermeture :
        // l'élément d'origine serait encore là, recevrait le focus puis disparaîtrait.
        focus.current = null;
        onNavigate?.(selfServeTarget);
      }
      onClose();
    }
    catch (error) {
      if (mounted.current) setState(definitive(error) ? "rejected" : "retry");
    }
    finally {
      sending.current = false;
    }
  };

  const actionsRevealed = activation && (revealed || mediaFailed);

  const close = () => {
    // Dismissing (X / Escape) counts as "later"; a failed call must not trap the elu in the modal.
    if (state === "ready") void onRespond(openingId, "later").catch(() => undefined);
    if (state === "thanks" || state === "rejected" || state === "ready") onClose();
  };

  const failMedia = () => {
    setMediaFailed(true);
    reveal();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        hasTitle
        showClose={false}
        className="advent-teaser-dialog gap-0 overflow-x-hidden overflow-y-auto rounded-3xl border-0 p-0 sm:max-w-2xl"
        onOpenAutoFocus={() => {
          focus.current = returnFocus ?? document.activeElement as HTMLElement;
        }}
        onCloseAutoFocus={(event) => {
          const target = focus.current?.isConnected ? focus.current : document.querySelector<HTMLElement>('[data-sidebar="trigger"]');
          if (target) {
            event.preventDefault();
            target.focus();
          }
        }}
      >
        {state !== "sending" && state !== "retry" && (
          <DialogClose className="absolute right-3 top-3 z-10 flex size-11 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
            <X className="size-5" aria-hidden="true" />
            <span className="sr-only">Fermer la proposition</span>
          </DialogClose>
        )}
        {state === "thanks"
          ? (
            <div className="animate-onboarding-slide-in bg-card flex flex-col items-center gap-4 p-8 text-center sm:p-10">
              <img src={yeatiHello} alt="" aria-hidden="true" className="h-40 w-auto" />
              <DialogTitle className="text-xl font-semibold">Merci pour votre réponse</DialogTitle>
              <DialogDescription className="max-w-md">{thanksMessage(purpose, accountManagerAssigned)}</DialogDescription>
              <Button className="mt-2" onClick={onClose} ref={(node) => node?.focus()}>Fermer</Button>
            </div>
          )
          : (
            <>
              <div className="relative bg-muted">
                {campaign.media.kind === "image"
                  ? (
                    <img src={campaign.media.url} alt={campaign.media.alt} onError={() => setMediaFailed(true)} className="aspect-video w-full object-contain" />
                  )
                  : (
                    // No loop and no restart: an activation video stays frozen on its end screen.
                    <video src={campaign.media.url} poster={campaign.media.poster_url ?? undefined} autoPlay={!prefersReducedMotion()} muted controls={!actionsRevealed} playsInline preload="none" aria-label={campaign.media.alt || campaign.title} onError={failMedia} {...videoHandlers} className="aspect-video w-full object-contain">
                      {campaign.media.captions_url && <track kind="captions" src={campaign.media.captions_url} srcLang="fr" label="Français" default />}
                    </video>
                  )}
                {/* End-screen overlay on desktop (YouTube-style); too small on mobile, so it flows below the video. */}
                {activation && (
                  <div className={actionsRevealed ? "animate-upsell-fade-in motion-reduce:animate-none p-4 sm:absolute sm:inset-0 sm:flex sm:items-center sm:justify-center sm:bg-black/60 sm:p-6 sm:backdrop-blur-[2px]" : undefined}>
                    <ActivationActions
                      revealed={actionsRevealed}
                      disabled={state !== "ready"}
                      canSelfServe={selfServeTarget !== undefined}
                      onRespond={(answer) => void respond(answer)}
                    />
                  </div>
                )}
              </div>
              <div className="animate-onboarding-slide-in bg-card border-border flex flex-col gap-3 border-t p-6">
                <DialogTitle className="text-foreground text-xl font-semibold">{campaign.title}</DialogTitle>
                <DialogDescription>{campaign.description}</DialogDescription>
                {mediaFailed && (
                  <div>
                    <p role="alert">Le média ne peut pas être affiché. Vous pouvez toujours lire le texte et répondre.</p>
                    {onRefresh && state === "ready" && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          void onRefresh(openingId).then((result) => {
                            setSnapshot((current) => ({ ...current, campaign: { ...current.campaign, media: result.campaign.media } }));
                            setMediaFailed(false);
                          }).catch((error: unknown) => {
                            setMediaFailed(true);
                            if (definitive(error)) setState("rejected");
                          });
                        }}
                      >
                        Recharger le média
                      </Button>
                    )}
                  </div>
                )}
                {shownFailed && (
                  <p role="status">
                    La confirmation d’affichage a échoué. Vous pouvez répondre ;
                    votre réponse confirmera cet affichage.
                  </p>
                )}
                {state === "retry" && <p role="alert">Votre réponse n’a pas été confirmée. Réessayez pour envoyer la même réponse.</p>}
                {state === "rejected" && <p role="alert">Cette proposition n’est plus disponible. Fermez cette fenêtre pour poursuivre.</p>}
                <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end" aria-busy={state === "sending"}>
                  {!activation && <UpsellActions disabled={state !== "ready"} onRespond={(answer) => void respond(answer)} />}
                  {state === "retry" && (
                    <Button onClick={() => {
                      if (intent.current) void respond(intent.current);
                    }}
                    >
                      Réessayer
                    </Button>
                  )}
                  {state === "rejected" && <Button onClick={onClose}>Fermer</Button>}
                </div>
              </div>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
};
